/* rl-archive-shelves — lazy-mounts the Refind Live archive's per-category feeds.

   The archive stacks one <fw-embed-feed> per category. Mounting all of them
   eagerly meant one Firework CDN fetch per shelf and up to `max_videos * N`
   hydrated tiles on first paint, nearly all below the fold. Liquid therefore
   emits only the slot (.rl-archive__embed[data-rl-shelf]) plus its config as
   data-* attributes, and this file builds the feed when the slot nears the
   viewport.

   SCOPE — deliberately narrow. This file is about performance and layout
   stability for browsing replays. It is NOT the live-state machinery: that's
   rl-live-poll.js (swap the live hero in mid-page) and rl-fw-fallback.js
   (detect a herounit that upgraded but rendered nothing, and offer a reload).
   Those answer "is the live moment broken right now?" and must escalate when
   the answer is yes. A shelf that renders nothing just means no video carries
   that category's hashtag — a content gap, not a failure — so the correct
   response is to collapse quietly. Same observable, opposite response: keep
   the two apart. In particular this file does not listen to
   `fw:widget:render-outcome`; that undocumented event has exactly one
   consumer (rl-fw-fallback.js), where being wrong means a black screen
   mid-broadcast rather than a stale min-height.

   DEEP LINKS live here for the same reason. The header's EXCLUSIVE DROPS menu
   links at #drop-<label>, and the browser resolves that fragment against a
   document whose shelf slots are still 0-height — then this file arms ~400px of
   reserve on every one of them and the target slides out from under the reader.
   The anchor is only broken because of the lazy mount, so the fix belongs next
   to it rather than in the section's own script.

   THE SIZE FILTER lives here too. One shelf can carry a row of size toggles
   (see the section's comment), and both the lazy mount and a toggle build an
   <fw-embed-feed> for the same slot — so they have to agree on how: one
   buildFeed(), one sanitizeTag(), one spelling of the s-expression. Split
   across two files they would drift the first time a filter attribute changed,
   and the failure would be a silently wrong feed rather than an error.

   The Firework SDK itself still loads eagerly from the section — only the feed
   elements are deferred. The hero depends on that SDK being present.

   Always setAttribute BEFORE appendChild: Firework's custom elements read
   their filter attributes during connectedCallback and do not refetch when an
   attribute changes afterwards. (Same constraint the old pill-filter script
   worked around by recreating the element on every swap.)

   No no-JS fallback is needed — Firework renders nothing without JS anyway. */

(function () {
  'use strict';

  var SLOT = '.rl-archive__embed[data-rl-shelf]';

  // Start fetching before the shelf is actually on screen so the feed has a
  // head start on its network round-trip.
  var ROOT_MARGIN = '600px 0px';

  // "The feed has rendered something." Low enough that an empty-category
  // placeholder (~30-60px) still counts as painted.
  var READY_PX = 30;

  // Floor for releasing the reserved min-height when the feed never paints.
  // Deliberately NOT rl-fw-fallback.js's DEFENSIVE_MS (15s): that number is
  // sized so a slow live feed can't produce a false "offline" flash during a
  // broadcast. This one only governs how long an untagged category holds an
  // empty well before collapsing, so matching 15s would make a cosmetic gap
  // linger. Long enough to cover a slow async SDK, short enough not to sit
  // there. Don't "harmonise" the two — they have different jobs.
  var SETTLE_FALLBACK_MS = 10000;

  // How many extra intervals to wait when the SDK still hasn't registered the
  // element. Bounded, so a permanently blocked SDK settles instead of reserving
  // height forever.
  var MAX_SETTLE_DEFERRALS = 2;

  // Permit only hashtag-safe chars in the s-expression value. Normalisation
  // lives here and only here — Liquid passes the merchant's text through
  // trimmed and escaped — so there is one definition of a valid hashtag.
  // `\w-` keeps underscores and hyphens, both valid Firework identifiers
  // (the channel slug itself is `the_refind`).
  function sanitizeTag(tag) {
    return String(tag || '').replace(/[^\w-]/g, '');
  }

  // Every tag the slot is filtered by, in the order Firework should AND them:
  // the shelf's category, then the selected size when the shelf has toggles.
  // `(and shoes 38)` reads "tagged shoes AND 38", so a shoe live has to carry
  // both to appear under a size — the crew runbook states that as a rule.
  function tagsFor(slot) {
    // The category is required, the size is not. A size on its own would pull
    // every video in the channel tagged 38 — bags, jewelry, anything — under a
    // Shoes title, so no category means no feed at all. Liquid never emits one
    // without the other; this is the guard that keeps that true if it ever does.
    var category = sanitizeTag(slot.getAttribute('data-hashtag'));
    if (!category) return [];

    var size = sanitizeTag(slot.getAttribute('data-size'));
    return size ? [category, size] : [category];
  }

  // Attributes are set BEFORE the element is connected (see the note up top),
  // so this returns a detached element for the caller to append.
  function buildFeed(slot, tags) {
    var channel = slot.getAttribute('data-channel') || '';
    var mode = slot.getAttribute('data-mode') || 'row';
    var maxVideos = parseInt(slot.getAttribute('data-max-videos'), 10) || 0;

    var feed = document.createElement('fw-embed-feed');
    if (channel) feed.setAttribute('channel', channel);
    feed.setAttribute('mode', mode);
    if (maxVideos > 0) feed.setAttribute('max_videos', String(maxVideos));
    feed.setAttribute('hashtag', '(and ' + tags.join(' ') + ')');
    return feed;
  }

  var io = null;

  // Has Firework registered the custom element? A zero-height feed only means
  // "empty category" once the SDK has actually had its chance; before that it
  // just means "still loading".
  function sdkReady() {
    return !!(window.customElements && window.customElements.get('fw-embed-feed'));
  }

  // Drop the reserved min-height once the feed has painted, so a category with
  // no tagged replays collapses to its title and rule rather than holding open
  // an empty well.
  function settleWhenPainted(slot, feed) {
    if (slot.classList.contains('is-settled')) return;
    if (slot.__rlShelfRo || slot.__rlShelfTimer) return; // already watching

    function settle() {
      if (slot.__rlShelfRo) { slot.__rlShelfRo.disconnect(); slot.__rlShelfRo = null; }
      if (slot.__rlShelfTimer) { clearTimeout(slot.__rlShelfTimer); slot.__rlShelfTimer = null; }
      slot.classList.add('is-settled');
    }

    // The timer can't tell "empty category" from "slow feed" on its own, and
    // guessing wrong collapses the stack and then re-expands it under the
    // reader. Defer while the SDK is demonstrably still absent — that's the one
    // case where a zero-height feed definitely isn't a verdict yet.
    var deferrals = 0;
    function onTimeout() {
      if (!sdkReady() && deferrals < MAX_SETTLE_DEFERRALS) {
        deferrals++;
        slot.__rlShelfTimer = setTimeout(onTimeout, SETTLE_FALLBACK_MS);
        return;
      }
      settle();
    }

    if (typeof ResizeObserver === 'function') {
      slot.__rlShelfRo = new ResizeObserver(function () {
        if (feed.offsetHeight > READY_PX) settle();
      });
      slot.__rlShelfRo.observe(feed);
    }
    slot.__rlShelfTimer = setTimeout(onTimeout, SETTLE_FALLBACK_MS);
  }

  function mount(slot) {
    // Already populated (theme-editor re-entry, or the no-IntersectionObserver
    // path running twice): don't build a second feed, but DO fall through to
    // the settle watch — returning early here would strand the reserve.
    var existing = slot.querySelector('fw-embed-feed');
    if (existing) {
      settleWhenPainted(slot, existing);
      return;
    }

    // No usable category tag — the attribute is missing, or it normalises to
    // nothing (merchant typed `#`, or punctuation only). Mounting without a
    // filter would render the entire channel under a category title, so leave
    // the shelf empty and release its reserve — a visibly empty shelf is the
    // honest signal for a misconfigured line.
    var tags = tagsFor(slot);
    if (!tags.length) {
      slot.classList.add('is-settled');
      return;
    }

    var feed = buildFeed(slot, tags);
    slot.appendChild(feed);
    settleWhenPainted(slot, feed);
  }

  function observer() {
    if (io || typeof IntersectionObserver !== 'function') return io;
    io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          mount(entry.target);
        });
      },
      { rootMargin: ROOT_MARGIN }
    );
    return io;
  }

  function mountAll(scope) {
    var obs = observer();
    (scope || document).querySelectorAll(SLOT).forEach(function (slot) {
      // Arm the height reserve here rather than in the stylesheet, so a page
      // where this script never ran shows collapsed shelves instead of a stack
      // of empty wells. See the .is-armed rule in section-refind-live.css.
      slot.classList.add('is-armed');
      if (obs) obs.observe(slot);
      else mount(slot); // no IntersectionObserver support → mount immediately
    });
  }

  // ── Size filter ───────────────────────────────────────────────────────────

  // The fade itself is a CSS transition on --dur-hover (see the crossfade rules
  // in section-refind-live.css); this timer only decides when the faded element
  // can leave the DOM. Read the token rather than restate it — same idiom as
  // global.js's nav-drawer close timer — so retuning the design system doesn't
  // yank the outgoing feed out mid-fade. The slack covers transitionend timing.
  var durHover = getComputedStyle(document.documentElement).getPropertyValue('--dur-hover').trim();
  var FADE_MS = parseInt(durHover, 10) || 300;
  var FADE_CLEANUP_MS = FADE_MS + 20;

  // How long to leave the outgoing feed on screen when the incoming one never
  // reports a height. Much shorter than SETTLE_FALLBACK_MS on purpose: that one
  // governs a shelf nobody asked for, this one runs right after a click, where
  // a stalled swap reads as a broken control rather than a slow page.
  var SWAP_FALLBACK_MS = 2500;

  // A swap outlives the click that started it — an observer and two timers — so
  // the slot carries a record of the one in flight. Without it a second click
  // leaves the first swap's timers running against a feed that is no longer
  // there, and its cleanup releases the height lock the second swap just took:
  // the slot is settled by then, so nothing holds it open and the page jumps by
  // a shelf's height. Everything a swap starts is cancellable from here.
  function cancelSwap(slot) {
    var swap = slot.__rlSwap;
    if (!swap) return;
    slot.__rlSwap = null;

    if (swap.ro) swap.ro.disconnect();
    clearTimeout(swap.fade);
    clearTimeout(swap.hold);
    clearTimeout(swap.cleanup);

    // Whatever that swap was fading out never finished leaving; drop it now
    // rather than stack a second overlay on the slot.
    var stale = slot.querySelectorAll('fw-embed-feed.is-stale');
    for (var i = 0; i < stale.length; i++) stale[i].remove();
  }

  function swapFeed(slot) {
    // Not mounted yet — the reader reached the toggles before the slot came
    // into range. Nothing to swap; the lazy mount reads data-size when it runs.
    var oldFeed = slot.querySelector('fw-embed-feed:not(.is-stale)');
    if (!oldFeed) return;

    var tags = tagsFor(slot);
    if (!tags.length) return;

    cancelSwap(slot);

    var newFeed = buildFeed(slot, tags);

    // Lock the slot to the height on screen so the stack below doesn't jump
    // while the incoming feed boots. An existing lock is kept rather than
    // re-measured: mid-swap the feed in flow hasn't painted yet, so measuring
    // now would lock the slot to zero — the collapse this is here to prevent.
    if (!slot.style.height) {
      var lockedHeight = oldFeed.offsetHeight;
      if (lockedHeight > 0) slot.style.height = lockedHeight + 'px';
    }

    // Old feed becomes an overlay, new feed enters flow underneath it and boots
    // out of sight. Once it has painted, fade the old one out and drop it.
    oldFeed.classList.add('is-stale');
    slot.appendChild(newFeed);

    var swap = { ro: null, fade: null, hold: null, cleanup: null, faded: false };
    slot.__rlSwap = swap;

    function fadeOutOld() {
      if (swap.faded) return;
      swap.faded = true;
      clearTimeout(swap.fade);
      oldFeed.classList.add('is-fading');
      swap.cleanup = setTimeout(function () {
        if (oldFeed.parentNode) oldFeed.parentNode.removeChild(oldFeed);
      }, FADE_CLEANUP_MS);
    }

    function releaseHeight() {
      if (swap.ro) { swap.ro.disconnect(); swap.ro = null; }
      clearTimeout(swap.hold);
      slot.style.height = '';
    }

    if (typeof ResizeObserver === 'function') {
      swap.ro = new ResizeObserver(function () {
        if (newFeed.offsetHeight <= READY_PX) return;
        // The incoming feed occupies the space itself now, so the lock has
        // nothing left to hold and the outgoing overlay has nothing to hide.
        fadeOutOld();
        releaseHeight();
      });
      swap.ro.observe(newFeed);
    }

    // Two deadlines, deliberately different lengths. The short one stops a
    // stalled feed from leaving the old one on screen looking like the toggle
    // did nothing — fading is safe blind. Releasing the height is not: doing it
    // on the same deadline collapses the shelf every time Firework is merely
    // slow, which is the jump the lock exists to prevent. So the lock runs to
    // the same deadline the lazy mount uses to decide "still loading, or just
    // empty?" — by then an empty size should collapse to its title.
    swap.fade = setTimeout(fadeOutOld, SWAP_FALLBACK_MS);
    swap.hold = setTimeout(releaseHeight, SETTLE_FALLBACK_MS);

    // The settle watch may still be pointed at the feed this swap just removed
    // (a toggle pressed before the shelf's first paint). Left alone it observes
    // a detached node for ever, and the reserve can only come off via its own
    // fallback — a 400px well over an empty size for ten seconds or more.
    if (!slot.classList.contains('is-settled')) {
      if (slot.__rlShelfRo) { slot.__rlShelfRo.disconnect(); slot.__rlShelfRo = null; }
      if (slot.__rlShelfTimer) { clearTimeout(slot.__rlShelfTimer); slot.__rlShelfTimer = null; }
      settleWhenPainted(slot, newFeed);
    }
  }

  function sizeTabs(row) {
    return Array.prototype.slice.call(row.querySelectorAll('[role="tab"]'));
  }

  function selectSize(row, tab, focusIt) {
    sizeTabs(row).forEach(function (other) {
      var on = other === tab;
      // aria-selected is the state; the stylesheet reads it directly, so there
      // is no parallel is-active class that can fall out of step with it.
      other.setAttribute('aria-selected', on ? 'true' : 'false');
      other.setAttribute('tabindex', on ? '0' : '-1');
    });
    if (focusIt) tab.focus();

    var slot = document.getElementById(tab.getAttribute('aria-controls') || '');
    if (!slot) return;
    slot.setAttribute('data-size', tab.getAttribute('data-rl-size') || '');
    // The panel is named by whichever size is showing in it.
    slot.setAttribute('aria-labelledby', tab.id);
    swapFeed(slot);
  }

  function tabFrom(event) {
    var el = event.target;
    if (!el || typeof el.closest !== 'function') return null;
    return el.closest('.rl-archive__sizes [role="tab"]');
  }

  // Delegated from the document rather than bound per row: the theme editor
  // replaces the section's markup wholesale on every settings edit, and a
  // delegated listener needs no rebinding — and leaks nothing — when it does.
  document.addEventListener('click', function (event) {
    var tab = tabFrom(event);
    if (!tab) return;
    if (tab.getAttribute('aria-selected') === 'true') return;
    selectSize(tab.closest('.rl-archive__sizes'), tab, false);
  });

  // Roving tabindex: arrows move focus and selection together (automatic
  // activation — the panel is one feed, so there is nothing to tab into first).
  // Enter and Space need no handling; these are <button>s, so they click.
  //
  // Left/Right and Home/End only. The row is a horizontal tablist — no
  // aria-orientation, which means horizontal — so Up/Down belong to the page:
  // claiming them would eat a keyboard reader's scroll and swap the shelf under
  // them instead.
  document.addEventListener('keydown', function (event) {
    var tab = tabFrom(event);
    if (!tab) return;

    var row = tab.closest('.rl-archive__sizes');
    var tabs = sizeTabs(row);
    var index = tabs.indexOf(tab);
    if (index === -1) return;

    var next = null;
    if (event.key === 'ArrowRight') {
      next = tabs[(index + 1) % tabs.length];
    } else if (event.key === 'ArrowLeft') {
      next = tabs[(index - 1 + tabs.length) % tabs.length];
    } else if (event.key === 'Home') {
      next = tabs[0];
    } else if (event.key === 'End') {
      next = tabs[tabs.length - 1];
    } else {
      return;
    }

    // Home on the first size, End on the last, or any arrow in a one-size row
    // resolves to the size already showing. Suppress the key either way — the
    // row owns it — but don't rebuild the feed for it: that is a CDN fetch and a
    // crossfade to the same thing. The click path declines the same no-op.
    event.preventDefault();
    if (next !== tab) selectSize(row, next, true);
  });

  // ── Deep links ────────────────────────────────────────────────────────────

  // How long to keep the anchor pinned while the stack resolves. Sized to
  // outlast one SETTLE_FALLBACK_MS verdict, because an empty category releasing
  // its reserve is exactly the event that drags the target off screen — and
  // that verdict is the last thing that moves the page on its own. Deliberately
  // not sized to cover MAX_SETTLE_DEFERRALS as well: a page where the SDK never
  // registers is one where nothing is going to paint anyway, and holding the
  // scroll position for half a minute is worse than a stale offset.
  var DEEP_LINK_HOLD_MS = SETTLE_FALLBACK_MS + 2000;

  // Inputs that mean the reader has taken over. `scroll` is deliberately absent
  // — our own scrollIntoView fires it, which would release the hold instantly.
  var TAKEOVER_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'];

  var releaseHold = null;

  // Only fragments this section actually owns. Anything else on the page —
  // another section's anchor, an app's — must be left alone.
  function deepLinkTarget() {
    var hash = window.location.hash;
    if (!hash || hash.length < 2) return null;
    var id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch (e) {
      return null; // malformed percent-encoding
    }
    if (id.indexOf('drop-') !== 0) return null;
    var el = document.getElementById(id);
    return el && el.classList.contains('rl-archive__group') ? el : null;
  }

  function holdDeepLink() {
    if (releaseHold) releaseHold();

    var target = deepLinkTarget();
    if (!target) return;

    // Re-assert now: every reserve was just armed, so wherever the browser's
    // own fragment scroll landed is already stale. No smooth behaviour here —
    // this is a correction to a jump that already happened, not a journey.
    target.scrollIntoView();

    var ro = null;
    var timer = null;

    function release() {
      releaseHold = null;
      // Nulled, not just disconnected: a deferred correction may already be
      // queued for the next frame, and it checks `ro` to know it was cancelled.
      if (ro) { ro.disconnect(); ro = null; }
      clearTimeout(timer);
      TAKEOVER_EVENTS.forEach(function (evt) {
        window.removeEventListener(evt, release);
      });
    }
    releaseHold = release;

    TAKEOVER_EVENTS.forEach(function (evt) {
      window.addEventListener(evt, release, { passive: true });
    });

    // Every height change in the stack moves the target. Watching the container
    // catches both directions: a feed painting taller than its reserve, and a
    // shelf above collapsing when it settles empty.
    //
    // The correction is deferred a frame to keep layout-forcing work out of the
    // observer callback. Note this is prophylaxis, not a fix for an observed
    // bug: adjusting scroll doesn't resize the observed box, so the callback
    // doesn't re-notify and no loop occurs — measured, with six rounds of
    // shelf collapse/grow above the target producing zero
    // "ResizeObserver loop completed with undelivered notifications" errors
    // either way. It stays because calling scrollIntoView() inline is the shape
    // that starts producing them the moment anything downstream does resize on
    // scroll, and a frame's delay is invisible here.
    if (typeof ResizeObserver === 'function') {
      ro = new ResizeObserver(function () {
        requestAnimationFrame(function () {
          // The hold can be released between the callback and the frame.
          if (!ro) return;
          target.scrollIntoView();
        });
      });
      document.querySelectorAll('.rl-archive__groups').forEach(function (groups) {
        ro.observe(groups);
      });
    }

    timer = setTimeout(release, DEEP_LINK_HOLD_MS);
  }

  function unmount(slot) {
    if (io) io.unobserve(slot);
    if (slot.__rlShelfRo) { slot.__rlShelfRo.disconnect(); slot.__rlShelfRo = null; }
    if (slot.__rlShelfTimer) { clearTimeout(slot.__rlShelfTimer); slot.__rlShelfTimer = null; }
    // A swap in flight when the editor re-renders would otherwise keep an
    // observer and two timers alive against a detached slot, once per edit.
    cancelSwap(slot);
  }

  // Order matters: arm the reserves first, then correct the fragment scroll
  // against the heights that arming just produced.
  function init() {
    mountAll();
    holdDeepLink();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // A menu item clicked while already on this page is a same-document
  // navigation — no reload, so init() never re-runs. The shelves are mounted by
  // then, but one can still settle mid-scroll, so take the hold again.
  window.addEventListener('hashchange', holdDeepLink);

  // Theme editor: the section's <script src> is re-injected via innerHTML on
  // re-render and does NOT re-execute, so without this every shelf would stay
  // an empty 400px well after any settings edit. Unload clears each slot's
  // observer and timer so repeated edits don't leak them.
  document.addEventListener('shopify:section:load', function (e) { mountAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll(SLOT).forEach(unmount);
    // The hold observes .rl-archive__groups and closes over a shelf element,
    // both of which this unload may be detaching.
    if (releaseHold) releaseHold();
  });
})();
